import isEmpty from 'lodash/isEmpty';
import React, { createContext, useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';

import { Maybe, useMembershipQuery, useWeddingQuery } from '../../generated/graphql';
import { marketToString } from './helpers';

export enum STEPS {
  BASIC_INFO = 0,
  ACCOUNT_CREATION,
}

interface OnboardingData {
  firstName?: Maybe<string>;
  lastName?: Maybe<string>;
  fianceFirstName?: Maybe<string>;
  fianceLastName?: Maybe<string>;
  themeId?: Maybe<number>;
  searchable?: Maybe<boolean>;
  vanityUrl?: Maybe<string>;
  weddingDate?: Maybe<string>;
  weddingLocation?: Maybe<string>;
  weddingDateTBA?: Maybe<boolean>;
  email?: Maybe<string>;
  password?: Maybe<string>;
  isLoggedIn?: boolean;
  canGoBack?: boolean;
  loading?: boolean;
  onboardingType?: OnboardingType;
}

interface OnboardingAction {
  type: string;
  payload?: OnboardingData;
}

interface OnboardingState extends OnboardingData {
  step: number;
}

export type OnboardingType = 'AI' | 'STANDARD';

interface OnboardingProps extends OnboardingState {
  onSubmit: (data: OnboardingAction) => void;
  onBack: () => void;
}

const reducer = (prevState: OnboardingState, { type, payload }: OnboardingAction) => {
  switch (type) {
    case 'SUBMIT_BASIC_INFO': {
      return {
        ...prevState,
        ...payload,
        step: STEPS.ACCOUNT_CREATION,
      };
    }
    case 'PROPS_CHANGED':
      return {
        ...prevState,
        ...payload,
        step: prevState.step,
      };
    case 'ACCOUNT_CREATED':
      return {
        ...prevState,
        ...payload,
        isLoggedIn: true,
        canGoBack: false,
      };
    case 'BACK':
      return {
        ...prevState,
        canGoBack: true,
        step: prevState.step - 1,
      };
    default:
      return prevState;
  }
};

const placeholderState = {
  firstName: null,
  lastName: null,
  fianceFirstName: null,
  fianceLastName: null,
  themeId: null,
  step: STEPS.BASIC_INFO,
  isLoggedIn: false,
  canGoBack: true,
  onSubmit: () => {},
  onBack: () => {},
  loading: true,
  onboardingType: undefined,
  searchable: true,
};

export const OnboardingContext = createContext<OnboardingProps>(placeholderState);

export const OnboardingProvider: React.FC = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const { data, loading: loadingMember } = useMembershipQuery();
  const { data: weddingData, loading: loadingWedding } = useWeddingQuery();
  const [state, dispatch] = useReducer(reducer, placeholderState);

  useEffect(() => {
    dispatch({
      type: 'PROPS_CHANGED',
      payload: {
        firstName: data?.member?.firstName,
        lastName: data?.member?.lastName,
        fianceFirstName: data?.member?.fianceFirstName,
        fianceLastName: data?.member?.fianceLastName,
        themeId: Number(id),
        isLoggedIn: !isEmpty(data?.member),
        weddingLocation: weddingData?.wedding?.market ? marketToString(weddingData?.wedding?.market) : '',
        weddingDate: weddingData?.wedding?.weddingDate || '',
        loading: loadingWedding || loadingMember,
      },
    });
  }, [data, id, weddingData, loadingMember, loadingWedding]);

  return (
    <OnboardingContext.Provider
      value={{
        ...state,
        onSubmit: ({ type, payload }) => {
          dispatch({
            type,
            payload,
          });
        },
        onBack: () => {
          dispatch({
            type: 'BACK',
          });
        },
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
